
import React, { useState, useEffect } from "react";
import TeacherLayout from "@/components/TeacherLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, MessageSquare, Lock, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { createApiUrl } from "@/lib/api";

interface TeacherProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  otp_verified: boolean;
  created_at: string;
}

interface WhatsAppCredentials {
  token: string;
  instance_id: string;
  created_at?: string;
  updated_at?: string;
}

const TeacherProfile = () => {
  const [profile, setProfile] = useState<TeacherProfile | null>(null);
  const [whatsappCredentials, setWhatsappCredentials] = useState<WhatsAppCredentials>({
    token: "",
    instance_id: ""
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
    fetchWhatsAppCredentials();
  }, []);

  const fetchProfile = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/teacher/login');
        return;
      }

      const response = await fetch(createApiUrl('/teacher/profile'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else if (response.status === 401) {
        localStorage.removeItem('teacherData');
        localStorage.removeItem('accessToken');
        navigate('/teacher/login');
      } else {
        throw new Error('Failed to fetch profile');
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
      toast({
        title: "Error",
        description: "Failed to load profile data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWhatsAppCredentials = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const response = await fetch(createApiUrl('/teacher/whatsapp-credentials'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setWhatsappCredentials(data);
      }
    } catch (error) {
      console.error('WhatsApp credentials fetch error:', error);
    }
  };

  const handleProfileChange = (field: keyof TeacherProfile, value: string) => {
    if (profile) {
      setProfile(prev => prev ? ({
        ...prev,
        [field]: value
      }) : null);
    }
  };

  const handleWhatsAppChange = (field: keyof WhatsAppCredentials, value: string) => {
    setWhatsappCredentials(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveProfile = async () => {
    if (!profile) return;
    
    setIsSaving(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const response = await fetch(createApiUrl('/teacher/profile'), {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: profile.name,
          email: profile.email,
          phone: profile.phone
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.user);
        toast({
          title: "Success",
          description: "Profile updated successfully"
        });
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveWhatsAppCredentials = async () => {
    if (!whatsappCredentials.token || !whatsappCredentials.instance_id) {
      toast({
        title: "Error",
        description: "Please fill in all WhatsApp credentials",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const response = await fetch(createApiUrl('/teacher/whatsapp-credentials'), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: whatsappCredentials.token,
          instance_id: whatsappCredentials.instance_id
        }),
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "WhatsApp credentials updated successfully"
        });
        fetchWhatsAppCredentials();
      } else {
        throw new Error('Failed to update WhatsApp credentials');
      }
    } catch (error) {
      console.error('WhatsApp credentials update error:', error);
      toast({
        title: "Error",
        description: "Failed to update WhatsApp credentials",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const changePassword = () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Error",
        description: "New password and confirm password don't match",
        variant: "destructive"
      });
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters long",
        variant: "destructive"
      });
      return;
    }

    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
    
    toast({
      title: "Success",
      description: "Password changed successfully"
    });
  };

  if (isLoading) {
    return (
      <TeacherLayout>
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm sm:text-base">Loading profile...</p>
          </div>
        </div>
      </TeacherLayout>
    );
  }

  if (!profile) {
    return (
      <TeacherLayout>
        <div className="text-center py-8 sm:py-12 p-4">
          <p className="text-gray-600 text-sm sm:text-base">Failed to load profile data</p>
        </div>
      </TeacherLayout>
    );
  }

  return (
    <TeacherLayout>
      <div className="space-y-4 sm:space-y-6">
        <div className="px-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2 mb-1 sm:mb-2">
            <User className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
            Profile Settings
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">Manage your account settings and preferences</p>
        </div>

        {/* Profile Information */}
        <Card>
          <CardHeader className="pb-3 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
              Profile Information
            </CardTitle>
            <p className="text-xs sm:text-sm text-gray-600">Update your personal information</p>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label htmlFor="name" className="text-sm">Full Name</Label>
                <Input
                  id="name"
                  value={profile.name}
                  onChange={(e) => handleProfileChange('name', e.target.value)}
                  placeholder="Enter your full name"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="email" className="text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                  placeholder="Enter your email"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="phone" className="text-sm">Phone Number</Label>
                <Input
                  id="phone"
                  value={profile.phone}
                  onChange={(e) => handleProfileChange('phone', e.target.value)}
                  placeholder="Enter your phone number"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="created_at" className="text-sm">Member Since</Label>
                <Input
                  id="created_at"
                  value={new Date(profile.created_at).toLocaleDateString()}
                  disabled
                  className="bg-gray-50 mt-1"
                />
              </div>
            </div>
            
            <div className="flex justify-end pt-2">
              <Button 
                onClick={saveProfile} 
                disabled={isSaving}
                className="bg-indigo-600 hover:bg-indigo-700 text-sm"
                size="sm"
              >
                <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                {isSaving ? "Saving..." : "Save Profile"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Integration */}
        <Card>
          <CardHeader className="pb-3 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
              WhatsApp Integration
            </CardTitle>
            <p className="text-xs sm:text-sm text-gray-600">Configure WhatsApp API credentials for notifications</p>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4">
            <div>
              <Label htmlFor="token" className="text-sm">WhatsApp API Token</Label>
              <Input
                id="token"
                type="password"
                value={whatsappCredentials.token}
                onChange={(e) => handleWhatsAppChange('token', e.target.value)}
                placeholder="Enter your WhatsApp API token"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="instance_id" className="text-sm">Instance ID</Label>
              <Input
                id="instance_id"
                value={whatsappCredentials.instance_id}
                onChange={(e) => handleWhatsAppChange('instance_id', e.target.value)}
                placeholder="Enter your WhatsApp instance ID"
                className="mt-1"
              />
            </div>
            
            {whatsappCredentials.updated_at && (
              <p className="text-xs sm:text-sm text-gray-500">
                Last updated: {new Date(whatsappCredentials.updated_at).toLocaleString()}
              </p>
            )}
            
            <div className="flex justify-end pt-2">
              <Button 
                onClick={saveWhatsAppCredentials} 
                disabled={isSaving}
                className="bg-green-600 hover:bg-green-700 text-sm"
                size="sm"
              >
                <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                {isSaving ? "Saving..." : "Save WhatsApp Credentials"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader className="pb-3 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
              Change Password
            </CardTitle>
            <p className="text-xs sm:text-sm text-gray-600">Update your password to keep your account secure</p>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4">
            <div>
              <Label htmlFor="currentPassword" className="text-sm">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                placeholder="Enter current password"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="newPassword" className="text-sm">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                placeholder="Enter new password"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPassword" className="text-sm">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                placeholder="Confirm new password"
                className="mt-1"
              />
            </div>
            
            <div className="flex justify-end pt-2">
              <Button 
                onClick={changePassword} 
                className="bg-red-600 hover:bg-red-700 text-sm"
                size="sm"
              >
                <Lock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Change Password
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </TeacherLayout>
  );
};

export default TeacherProfile;
